import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "../../css/ExternalLiveLocation.css";
import {
  JobInformationPanel,
  VehicleInformationPanel,
} from "../../components/mission-control/InfoPanels";
import MapOSView from "../../components/mission-control/MapOSView";
import TrackingUnavailableView from "../../components/mission-control/TrackingUnavailableView";
import { gatewayUrl, SERVICE_ENDPOINT } from "../../const";
import { gatewayInstance } from "../../axiosInstance";
import LoadingSpinner from "../../components/LoadingSpinner";
import axios from "axios";
import { getGatewayUrl } from "../../config";

const statusConst = {
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
};

const ExternalJobDashboard = () => {
  const { jobId } = useParams();
  const [jobData, setJobData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [jobLocations, setJobLocations] = useState([]);
  const [isLive, setIsLive] = useState(null);
  const [wsStatus, setWsStatus] = useState(statusConst.LOADING);
  const [loadingMessage, setLoadingMessage] = useState(
    "Fetching live tracking details..."
  );
  const [remainingCount, setRemainingCount] = useState(null);
  const [limitExceeded, setLimitExceeded] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await axios.get(
        `${gatewayUrl}${SERVICE_ENDPOINT.LOCATION}/public/track/${jobId}`
      );
      const data = response.data;

      if (data) {
        if (data.remainingCount !== undefined) {
          setRemainingCount(data.remainingCount);
          if (data.remainingCount === 0) {
            setLimitExceeded(true);
          }
        }

        setIsLive(data.liveLocation);
        setWsStatus(statusConst.SUCCESS);

        if (data.liveLocation) {
          setJobData(data.jobInfo);
          setVehicleData(data.vehicleInfo);

          const locations = [];
          if (data.startLocation) {
            locations.push({ ...data.startLocation, type: "start" });
          }
          if (data.viaLocations && Array.isArray(data.viaLocations)) {
            data.viaLocations.forEach((loc) => {
              locations.push({ ...loc, type: "via" });
            });
          }
          if (data.endLocation) {
            locations.push({ ...data.endLocation, type: "end" });
          }
          setJobLocations(locations);
        }
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        (error.response.data.message.toLowerCase().includes("limit") ||
          error.response.data.message.toLowerCase().includes("expired"))
      ) {
        setLimitExceeded(true);
        setRemainingCount(0);
        setWsStatus(statusConst.SUCCESS);
      } else {
        setWsStatus(statusConst.FAILURE);
        setLoadingMessage(
          "Failed to retrieve tracking data. Please check your connection."
        );
      }
    }
  }, [jobId]);

  const fetchLiveLocation = useCallback(
    async (vehicleNo) => {
      if (!vehicleNo || limitExceeded) return;

      try {
        const response = await axios.get(
          `${gatewayUrl}${SERVICE_ENDPOINT.LOCATION}/public/live-location/${vehicleNo}?token=${jobId}`
        );

        if (response.data) {
          setVehicleData((prev) => ({
            ...prev,
            ...response.data,
          }));
        }
      } catch (error) {
        
      }
    },
    [jobId, limitExceeded]
  );

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (!vehicleData?.vehicleNo || limitExceeded) return;

    fetchLiveLocation(vehicleData.vehicleNo);

    const intervalId = setInterval(() => {
      fetchLiveLocation(vehicleData.vehicleNo);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [vehicleData?.vehicleNo, fetchLiveLocation, limitExceeded]);

  if (wsStatus === statusConst.LOADING || wsStatus === statusConst.FAILURE) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <LoadingSpinner
          spinner={wsStatus === statusConst.FAILURE ? "error" : "gps-loader"}
          message={loadingMessage}
        />
      </div>
    );
  }

  if (limitExceeded) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          backgroundColor: "#f4f4f9",
          fontFamily: "Arial, sans-serif",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            backgroundColor: "#e74c3c",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: "20px",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 9999,
          }}
        >
          Views Remaining: 0
        </div>
        <h2 style={{ color: "#e74c3c", marginBottom: "10px" }}>
          Tracking Limit Exceeded
        </h2>
        <p style={{ color: "#555", fontSize: "16px" }}>
          Your view limit for this tracking link is over or the link has expired.
        </p>
      </div>
    );
  }

  if (isLive === false) {
    return <TrackingUnavailableView  remainingCount={remainingCount}/>;
  }

  const vehicleLocation =
    vehicleData?.latitude && vehicleData?.longitude
      ? {
          lat: vehicleData.latitude,
          lng: vehicleData.longitude,
          heading:
            vehicleData.heading ||
            vehicleData.bearing ||
            vehicleData.direction ||
            0,
        }
      : null;

  return (
    <div
      className="external-dashboard-container"
      style={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {remainingCount !== null && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            backgroundColor: remainingCount <= 5 ? "#e74c3c" : "#2ecc71",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: "20px",
            fontWeight: "bold",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 9999,
            transition: "background-color 0.3s ease",
          }}
        >
          Views Remaining: {remainingCount}
        </div>
      )}

      <div
        className="sidebar-left"
        style={{
          width: "360px",
          minWidth: "360px",
          height: "100%",
          background: "#ffffff",
          boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          padding: "20px",
          boxSizing: "border-box",
          overflowY: "auto",
          zIndex: 10,
        }}
      >
        <div className="sidebar-section">
          <h3
            style={{
              margin: "0 0 15px 0",
              color: "#333",
              fontSize: "18px",
              borderBottom: "2px solid #f0f0f0",
              paddingBottom: "8px",
            }}
          >
            Job Information
          </h3>
          <JobInformationPanel jobData={jobData} />
        </div>
        <div className="sidebar-section">
          <h3
            style={{
              margin: "0 0 15px 0",
              color: "#333",
              fontSize: "18px",
              borderBottom: "2px solid #f0f0f0",
              paddingBottom: "8px",
            }}
          >
            Vehicle Information
          </h3>
          <VehicleInformationPanel vehicleData={vehicleData} />
        </div>
      </div>
      <div
        className="map-content"
        style={{ flex: 1, height: "100%", position: "relative" }}
      >
        <MapOSView
          vehicleLocation={vehicleLocation}
          jobLocations={jobLocations}
        />
      </div>
    </div>
  );
};

export default ExternalJobDashboard;